import type { World } from './world';

// ─────────────────────────────────────────────────────────────────────────────
// The ocean as blocks, Terraria-style. Each class's map (world.ts) is turned
// into a grid of 32-unit blocks: sand on top, stone below, coral on the reef,
// wood in the wrecks, crystal near the caves, dark stone in the trenches and
// the deep, temple bricks around the Sunken Temples, and unbreakable bedrock at
// the edges. Students' changes (mined or placed blocks,
// shared by the whole class) are laid over the top.
//
// Block ids match supabase/migrations/20261013_sandbox.sql and 20261021_big_ocean.sql.
// ─────────────────────────────────────────────────────────────────────────────

export const B = 32;

export type BlockId = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 99;
export type BlockDef = { id: BlockId; item: string; name: string; color: string; dark: string; glow?: string; drill?: boolean; see?: boolean };

export const BLOCKS: Record<number, BlockDef> = {
  1: { id: 1, item: 'sand', name: 'Sand', color: '#e9d49d', dark: '#8f7f58' },
  2: { id: 2, item: 'stone', name: 'Stone', color: '#8c9099', dark: '#4b4e56' },
  3: { id: 3, item: 'coral', name: 'Coral', color: '#ff8d8d', dark: '#8a4a55' },
  4: { id: 4, item: 'crystal', name: 'Crystal', color: '#7ef0ff', dark: '#3d6470', glow: '#7ef9ff', drill: true },
  5: { id: 5, item: 'wood', name: 'Wood', color: '#8f5f39', dark: '#4a3322' },
  6: { id: 6, item: 'darkstone', name: 'Dark stone', color: '#565168', dark: '#2a2834', drill: true },
  7: { id: 7, item: 'glass', name: 'Glass', color: '#cdeeff', dark: '#5f7a88', see: true },
  8: { id: 8, item: 'brick', name: 'Stone bricks', color: '#b4b8c2', dark: '#55585f' },
  9: { id: 9, item: 'coral_brick', name: 'Coral bricks', color: '#ffb0bd', dark: '#8a5560' },
  10: { id: 10, item: 'planks', name: 'Planks', color: '#c99860', dark: '#5e4429' },
  11: { id: 11, item: 'lamp', name: 'Crystal lamp', color: '#fff4a8', dark: '#6e6640', glow: '#fff4a8' },
  12: { id: 12, item: 'temple_brick', name: 'Temple bricks', color: '#4f6b6a', dark: '#22302f', drill: true },
  // The heart of a habitat: placed inside something you've built, it draws fish to live there (habitats.ts).
  13: { id: 13, item: 'beacon', name: 'Fish beacon', color: '#ff9ff3', dark: '#7a3f75', glow: '#ff9ff3' },
  99: { id: 99, item: 'bedrock', name: 'Bedrock', color: '#2c2a35', dark: '#18171d' },
};
export const blockByItem = (item: string) => Object.values(BLOCKS).find(b => b.item === item && b.id !== 99);
export const PLACEABLE = Object.values(BLOCKS).filter(b => b.id !== 99).map(b => b.item);

export type TileEdit = { tx: number; ty: number; tile: number; student_id?: string | null; updated_at?: string };

export class Blocks {
  readonly cols: number;
  readonly rows: number;
  /** What the map started as. */
  readonly base: Uint8Array;
  /** What it is now (base + the class's changes). */
  readonly now: Uint8Array;
  private world: World;
  /** Bumped per 16×16 chunk when a block in it changes, so the renderer redraws just that chunk. */
  readonly chunkVersion = new Map<string, number>();
  /** Cells the class has changed to something other than the map's own block (for habitats). */
  readonly placed = new Map<number, number>();
  /** Goes up whenever any block changes. */
  version = 0;

  constructor(world: World) {
    this.world = world;
    this.cols = Math.ceil(world.w / B);
    this.rows = Math.ceil(world.h / B);
    this.base = buildBase(world, this.cols, this.rows);
    this.now = this.base.slice();
  }

  idx(tx: number, ty: number) { return ty * this.cols + tx; }
  inside(tx: number, ty: number) { return tx >= 0 && ty >= 0 && tx < this.cols && ty < this.rows; }
  at(tx: number, ty: number): number { return this.inside(tx, ty) ? this.now[this.idx(tx, ty)] : 99; }
  baseAt(tx: number, ty: number): number { return this.inside(tx, ty) ? this.base[this.idx(tx, ty)] : 99; }
  solid(tx: number, ty: number) { return this.at(tx, ty) !== 0; }

  set(tx: number, ty: number, tile: number) {
    if (!this.inside(tx, ty)) return;
    const i = this.idx(tx, ty);
    if (this.now[i] === tile) return;
    this.now[i] = tile;
    if (tile !== 0 && tile !== this.base[i]) this.placed.set(i, tile); else this.placed.delete(i);
    this.version++;
    // Neighbouring chunks too: outlines depend on the blocks around.
    for (const [dx, dy] of [[0, 0], [-1, 0], [1, 0], [0, -1], [0, 1]]) {
      const k = `${Math.floor((tx + dx) / 16)},${Math.floor((ty + dy) / 16)}`;
      this.chunkVersion.set(k, (this.chunkVersion.get(k) ?? 0) + 1);
    }
  }

  /** Lay the class's changes over the base: every stored cell, replacing whatever was there. */
  apply(edits: TileEdit[]) { for (const e of edits) this.set(e.tx, e.ty, e.tile); }

  /** Back to the base map (before applying a fresh full set of edits). */
  reset() {
    for (let i = 0; i < this.now.length; i++) {
      if (this.now[i] !== this.base[i]) this.set(i % this.cols, Math.floor(i / this.cols), this.base[i]);
    }
  }

  solidAtPoint(x: number, y: number) {
    if (y < 0) return false;
    return this.solid(Math.floor(x / B), Math.floor(y / B));
  }
  circleHits(x: number, y: number, r: number) {
    if (this.solidAtPoint(x, y)) return true;
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      if (this.solidAtPoint(x + Math.cos(a) * r, y + Math.sin(a) * r)) return true;
    }
    return false;
  }
  /** The first solid block at or below a point (for sitting things on the ground). */
  groundBelow(x: number, y: number): number | null {
    const tx = Math.floor(x / B);
    for (let ty = Math.max(0, Math.floor(y / B)); ty < this.rows; ty++) if (this.solid(tx, ty)) return ty * B;
    return null;
  }
  ceilingAbove(x: number, y: number): number | null {
    const tx = Math.floor(x / B);
    for (let ty = Math.min(this.rows - 1, Math.floor(y / B)); ty >= 0; ty--) if (this.solid(tx, ty)) return (ty + 1) * B;
    return null;
  }
  get worldRef() { return this.world; }
}

/** Turn a map into blocks, from its collision grid (8 units) and its features. */
function buildBase(world: World, cols: number, rows: number): Uint8Array {
  const { data, w: gw, h: gh } = world.collision();
  const k = B / 8;
  const out = new Uint8Array(cols * rows);
  const solidCell = (tx: number, ty: number) => {
    let n = 0;
    for (let y = 0; y < k; y++) for (let x = 0; x < k; x++) {
      const gx = tx * k + x, gy = ty * k + y;
      if (gx >= gw || gy >= gh || data[gy * gw + gx]) n++;
    }
    return n >= (k * k) / 2;
  };
  const hull = world.hullPath();
  const zones = world.zones;
  const zoneAt = (x: number) => { let lo = 0, hi = zones.length - 1; while (lo < hi) { const m = (lo + hi + 1) >> 1; if (zones[m].x0 <= x) lo = m; else hi = m - 1; } return zones[lo].id; };
  const lamps = new Set(world.lamps.map(([x, y]) => Math.floor(y / B) * cols + Math.floor(x / B)));
  const probe = document.createElement('canvas').getContext('2d')!;
  const rnd = (tx: number, ty: number, salt: number) => {
    let h = (tx * 374761393 + ty * 668265263 + salt * 2147483647) | 0;
    h = Math.imul(h ^ (h >>> 13), 1274126177);
    return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
  };
  const nearChamber = (x: number, y: number) => [...world.chambers.map(c => c.e), ...world.pockets].some(e => {
    const d = Math.sqrt(((x - e.x) / (e.rx + 90)) ** 2 + ((y - e.y) / (e.ry + 90)) ** 2);
    return d <= 1;
  });

  for (let tx = 0; tx < cols; tx++) {
    let depthIntoGround = -1;
    for (let ty = 0; ty < rows; ty++) {
      const i = ty * cols + tx;
      if (!solidCell(tx, ty)) { depthIntoGround = -1; out[i] = 0; continue; }
      depthIntoGround++;
      const x = tx * B + B / 2, y = ty * B + B / 2;
      const zone = zoneAt(x);
      let id: number;
      if (lamps.has(i)) id = 11;
      else if (y > 3600 && world.templeSpace(x, y, 96)) id = 12;
      else if (hull && y < 2400 && probe.isPointInPath(hull, x, y)) id = 5;
      else if (zone === 'trench' && y > world.floorY(x) - 40 && y > 2000) id = 6;
      else if (y > 3000) id = 6;
      else if (nearChamber(x, y) && rnd(tx, ty, 1) < 0.22) id = 4;
      else if (depthIntoGround === 0 && zone === 'reef' && y < world.floorY(x) + 90) id = rnd(tx, ty, 2) < 0.55 ? 3 : 1;   // coral on the open reef, not cave floors
      else if (depthIntoGround <= 1 && zone !== 'trench') id = 1;
      else id = 2;
      out[i] = id;
    }
  }
  // Unbreakable edges: the bottom two rows and the far left and right columns.
  for (let tx = 0; tx < cols; tx++) for (let ty = rows - 2; ty < rows; ty++) out[ty * cols + tx] = 99;
  for (let ty = 0; ty < rows; ty++) {
    if (out[ty * cols] !== 0) out[ty * cols] = 99;
    if (out[ty * cols + cols - 1] !== 0) out[ty * cols + cols - 1] = 99;
  }
  return out;
}
